import React, { useMemo } from 'react';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import type { ProposalBundle, ProposalItem } from '../../api/proposalBundles';
import { formatProposalPrice } from '../../utils/priceFormatter';
import { formatHotelLocationInfo } from '../../utils/routeInfoFormatter';

interface Props {
  bundle: ProposalBundle;
  productNumber: number;
  includeTax: boolean;
  onSelect?: (bundleId: number, itemId: number) => void;
  onClose?: (bundleId: number) => void;
}

const HotelCard: React.FC<Props> = ({ bundle, productNumber, includeTax, onSelect, onClose }) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const locationInfo = useMemo(
    () => formatHotelLocationInfo(bundle.destination, bundle.departureDate),
    [bundle.destination, bundle.departureDate]
  );

  const handleSelect = (itemId: number) => {
    onSelect?.(bundle.id, itemId);
  };

  const handleClose = () => {
    onClose?.(bundle.id);
  };

  return (
    <div style={styles.card}>
      <button type="button" style={styles.closeButton} onClick={handleClose}>
        ×
      </button>

      <div style={styles.header}>
        <span style={styles.productBadge}>商品{productNumber}</span>
        <span style={styles.categoryLabel}>🏨 宿泊施設{locationInfo}</span>
      </div>

      <table style={styles.table}>
        <thead>
          <tr style={styles.tableHeaderRow}>
            <th style={styles.tableHeaderCell}>ホテル名</th>
            <th style={styles.tableHeaderCell}>部屋タイプ・食事</th>
            <th style={styles.tableHeaderCell}>料金</th>
          </tr>
        </thead>
        <tbody>
          {bundle.items.map((item, index) => (
            <HotelRow
              key={item.id}
              item={item}
              index={index}
              includeTax={includeTax}
              onSelect={() => handleSelect(item.id)}
              theme={theme}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};

interface HotelRowProps {
  item: ProposalItem;
  index: number;
  includeTax: boolean;
  onSelect: () => void;
  theme: AITTheme;
}

const HotelRow: React.FC<HotelRowProps> = ({ item, index, includeTax, onSelect, theme }) => {
  const styles = useMemo(() => getRowStyles(theme, item.selected ?? false), [theme, item.selected]);
  const circleNumber = ['①', '②', '③', '④', '⑤'][index] || `${index + 1}`;

  const hotelName = item.title || '-';
  const roomInfo = item.subtitle || item.seatClass || '-';
  const tags = item.aiHighlightTags || [];
  const priceDisplay = formatProposalPrice({
    price: item.price,
    priceWithTax: item.priceWithTax,
    includeTax
  });

  const handleClick = () => {
    if (!item.selected) {
      onSelect();
    }
  };

  return (
    <tr style={styles.row}>
      <td style={styles.cellName}>
        <div style={styles.nameContainer}>
          <div style={styles.numberCircle}>{circleNumber}</div>
          <span>{hotelName}</span>
        </div>
      </td>
      <td style={styles.cell}>
        {roomInfo}
        {tags.length > 0 && (
          <>
            <br />
            {tags.map(tag => (
              <span key={tag} style={styles.tagBadge}>
                {tag}
              </span>
            ))}
          </>
        )}
      </td>
      <td style={styles.cellPrice}>
        <div style={styles.priceContainer}>
          <span>{priceDisplay}</span>
          <button type="button" style={styles.selectButton} onClick={handleClick}>
            選択
          </button>
        </div>
      </td>
    </tr>
  );
};

const getStyles = (theme: AITTheme) => ({
  card: {
    backgroundColor: theme.whiteColor,
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '20px',
    border: `2px solid ${theme.linkColor}`,
    position: 'relative' as const
  },
  closeButton: {
    position: 'absolute' as const,
    top: '12px',
    right: '12px',
    backgroundColor: '#f5f5f5',
    color: '#666',
    border: 'none',
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    fontSize: '18px',
    fontWeight: 300 as const,
    lineHeight: 1,
    padding: 0,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  header: {
    fontWeight: 700 as const,
    color: theme.linkColor,
    marginBottom: '16px',
    fontSize: '17px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  productBadge: {
    backgroundColor: theme.linkColor,
    color: '#ffffff',
    padding: '4px 12px',
    borderRadius: '16px',
    fontSize: '11px',
    fontWeight: 600 as const
  },
  categoryLabel: {
    fontSize: '14px'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const
  },
  tableHeaderRow: {
    backgroundColor: '#f5f5f5',
    borderBottom: '2px solid #ececec'
  },
  tableHeaderCell: {
    padding: '10px',
    textAlign: 'center' as const,
    fontSize: '13px'
  },
  tableHeaderCellRight: {
    padding: '10px',
    textAlign: 'center' as const,
    fontSize: '13px'
  },
  tableHeaderCellCenter: {
    padding: '10px',
    textAlign: 'center' as const,
    fontSize: '13px',
    width: '110px'
  }
});

const getRowStyles = (theme: AITTheme, selected: boolean) => ({
  row: {
    borderBottom: '1px solid #ececec',
    backgroundColor: selected ? '#e6f4fb' : 'transparent'
  },
  cell: {
    padding: '12px 8px',
    fontSize: '13px',
    verticalAlign: 'middle' as const
  },
  cellName: {
    padding: '12px 8px',
    fontSize: '13px',
    fontWeight: 500 as const,
    verticalAlign: 'middle' as const
  },
  nameContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  cellPrice: {
    padding: '12px 8px',
    textAlign: 'right' as const,
    verticalAlign: 'middle' as const
  },
  priceContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '10px',
    fontWeight: 700 as const,
    color: theme.linkColor,
    fontSize: '15px'
  },
  numberCircle: {
    width: '28px',
    height: '28px',
    minWidth: '28px',
    backgroundColor: theme.linkColor,
    color: '#ffffff',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700 as const,
    fontSize: '12px'
  },
  tagBadge: {
    backgroundColor: '#f0f9f4',
    color: '#4a9464',
    padding: '2px 8px',
    borderRadius: '10px',
    fontSize: '11px',
    marginRight: '4px'
  },
  selectButton: {
    backgroundColor: theme.linkColor,
    color: '#ffffff',
    border: 'none',
    padding: '8px 16px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 600 as const,
    cursor: selected ? 'default' : 'pointer',
    whiteSpace: 'nowrap' as const,
    opacity: selected ? 0.5 : 1
  }
});

export default HotelCard;
